/**
 * Afdeling ("team") bucketing for every overview that shows people from
 * more than one team.
 *
 * Deliberately ONE module rather than a copy per feature: the option list
 * and the filter predicate must agree, otherwise an employee can fall
 * between the two and disappear from every bucket. Consumed by both
 * `features/aanwezigheid` and `features/werkrooster`.
 */

/** Sentinel for employees without a resolvable department — never a real `departments.id`. */
export const GEEN_AFDELING = -1;

/** Minimal shape both `AttendanceEmployeeEntry` and `EmployeeWithSchedule` satisfy. */
export interface MetAfdeling {
  departmentId?: number | null;
  departmentName?: string | null;
}

/**
 * Which bucket an employee belongs to. A `departmentId` without a
 * resolvable `departmentName` counts as "geen afdeling": listing it under
 * an unnamed option is worse than grouping it with the other strays.
 */
export function afdelingKey(employee: MetAfdeling): number {
  return employee.departmentId != null && employee.departmentName ? employee.departmentId : GEEN_AFDELING;
}

/**
 * Build the `[id, label]` options for a department filter from whatever is
 * actually in the current response — never a hardcoded list. Named
 * departments come first, sorted on their Dutch label; the "Geen afdeling"
 * bucket is appended only when someone actually lands in it.
 */
export function afdelingOpties(employees: MetAfdeling[]): Array<[number, string]> {
  const named = new Map<number, string>();
  let hasZonderAfdeling = false;

  for (const employee of employees) {
    const key = afdelingKey(employee);
    if (key === GEEN_AFDELING) {
      hasZonderAfdeling = true;
    } else if (!named.has(key)) {
      // First label wins: a stable label beats one that flips with the
      // response order if the same id ever carries two names.
      named.set(key, employee.departmentName as string);
    }
  }

  const options: Array<[number, string]> = [...named.entries()].sort((a, b) => a[1].localeCompare(b[1], 'nl'));
  // UI-term is "team"; de code houdt `afdeling` aan omdat het één-op-één de
  // `departments`-tabel volgt. Bewuste splitsing, zie ook OrgScheduleFilters.
  if (hasZonderAfdeling) options.push([GEEN_AFDELING, 'Geen team']);
  return options;
}
